import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, CircleHelp } from "lucide-react";

const FAQS = [
  {
    q: "Do I need a credit card to start the free trial?",
    a: "Yes — we collect your payment information at sign-up to keep things smooth, but you won't be charged anything during the 14-day trial. We'll send you a reminder email 3 days before the trial ends so you have time to decide.",
  },
  {
    q: "Can I cancel before the trial ends?",
    a: "Absolutely. Cancel anytime during your 14-day trial and you won't be charged — no questions asked. You can cancel directly from your account settings in under 30 seconds.",
  },
  {
    q: "What's the difference between WealthScope and PortfolioIQ?",
    a: "WealthScope is your personal financial dashboard — net worth, buying power, DTI, and deal readiness. It answers 'can I afford to invest right now?' PortfolioIQ is your property management intelligence — cap rate, DSCR, cash-on-cash, and lender/CPA reports. It answers 'how are my properties performing?' The Bundle gives you both.",
  },
  {
    q: "Can I switch plans after I sign up?",
    a: "Yes — you can upgrade, downgrade, or switch between monthly and annual billing at any time from your account dashboard. If you upgrade mid-cycle, we'll prorate the difference.",
  },
  {
    q: "How does the 17% annual discount work?",
    a: "Choose 'Annual' billing when you sign up (or switch in settings) and you'll pay 17% less than paying monthly all year. The discount applies immediately and you can still cancel — we'll refund the unused months.",
  },
  {
    q: "Does VaWeNa connect to my bank accounts?",
    a: "WealthScope integrates with Plaid to connect checking, savings, and investment accounts — the same technology used by most banking apps. You can also enter data manually if you prefer. PortfolioIQ works with property data you enter directly.",
  },
  {
    q: "Is my financial data secure?",
    a: "Yes. All data is encrypted at rest (AES-256) and in transit (TLS 1.3). We never sell or share your data. We're nearly SOC 2 Type II compliant — audit in final stages.",
  },
  {
    q: "What happens to my data if I cancel?",
    a: "You can export all your data before cancelling. After cancellation, your data is retained for 30 days in case you want to reactivate, then permanently deleted on request.",
  },
];

const FAQItem: React.FC<{ q: string; a: string; defaultOpen?: boolean }> = ({ q, a, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="dark:border-white/[0.08] border-blue-200 border-b last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
      >
        <span className="text-sm font-semibold dark:text-white text-gray-900 leading-snug">{q}</span>
        <ChevronDown
          className={`flex-shrink-0 w-4 h-4 dark:text-slate-400 text-gray-400 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="content"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-5 text-sm dark:text-slate-400 text-gray-500 leading-relaxed pr-8">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FAQSection: React.FC = () => {
  const mid = Math.ceil(FAQS.length / 2);
  const left = FAQS.slice(0, mid);
  const right = FAQS.slice(mid);

  return (
    <section className="px-4 sm:px-6 pb-20 mx-auto max-w-[760px]">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold tracking-widest dark:text-blue-400 text-blue-700 uppercase dark:bg-blue-600/15 bg-blue-100 rounded-full mb-4">
          <CircleHelp className="w-3.5 h-3.5" />
          FAQ
        </div>
        <h2 className="text-2xl sm:text-3xl font-extrabold dark:text-white text-gray-900 mb-3">
          Common questions
        </h2>
        <p className="text-sm dark:text-slate-400 text-gray-500">
          Everything you need to know before signing up.
        </p>
      </div>

      <div className="dark:bg-white/[0.03] bg-white border dark:border-white/[0.08] border-blue-300 rounded-2xl shadow-sm overflow-hidden">
        <div className="grid md:grid-cols-2">
          <div className="px-6">
            {left.map((faq, i) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} defaultOpen={i === 0} />
            ))}
          </div>
          <div className="px-6 md:border-l dark:md:border-white/[0.08] md:border-blue-200">
            {right.map((faq) => (
              <FAQItem key={faq.q} q={faq.q} a={faq.a} />
            ))}
          </div>
        </div>
      </div>

      <p className="text-center text-sm dark:text-slate-500 text-gray-400 mt-8">
        Still have questions?{" "}
        <a href="/contact" className="text-blue-400 hover:text-blue-300 font-medium transition-colors">
          Talk to our team →
        </a>
      </p>
    </section>
  );
};
